import { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { FormattedMessage } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcaseClock } from '@fortawesome/free-solid-svg-icons';
import { formatMessage } from 'i18n/CurrentLocale';
import { ReduxState, Vehicle, WeekDay, WorkingWeek } from 'types';
import { Moment } from 'moment';
import ErrorMessageBox from 'components/ErrorMessageBox';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { WeekDayTableRow } from 'components/workinghours/WeekDayTableRow';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import * as vehiclesActions from 'features/vehicles/vehiclesActions';
import { usePrevious } from 'myParkdUtils/customHooks';
import SubmitButton from 'components/SubmitButton';


export const orderedWeekdays = [
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY'
] as const;

const UNRESTRICTED_WORKING_DAY = [{ start: '00:00', end: '23:59' }];

export const unrestrictedWorkingWeek: WorkingWeek = {
    schedule: {
        'MONDAY': UNRESTRICTED_WORKING_DAY,
        'TUESDAY': UNRESTRICTED_WORKING_DAY,
        'WEDNESDAY': UNRESTRICTED_WORKING_DAY,
        'THURSDAY': UNRESTRICTED_WORKING_DAY,
        'FRIDAY': UNRESTRICTED_WORKING_DAY,
        'SATURDAY': UNRESTRICTED_WORKING_DAY,
        'SUNDAY': UNRESTRICTED_WORKING_DAY
    },
    version: "1"
}

type InvalidTimeSlotError = {
    day: WeekDay
}

export const checkForInvalidTimeSlots = (workingWeek: WorkingWeek | null): InvalidTimeSlotError | null => {
    if (!workingWeek) return null;

    for (const [weekday, daySchedule] of Object.entries(workingWeek?.schedule)) {
        if (daySchedule.length) {
            if (daySchedule[0].start === null || daySchedule[0].end === null) {
                return { day: weekday as WeekDay };
            }
            if (daySchedule[0].start > daySchedule[0].end) {
                return { day: weekday as WeekDay };
            }
        }
    }
    return null;
}

export const EditWorkingHoursLicensePlateModal = (props: EditWorkingHoursLicensePlateModalProps) => {
    const [workingHoursSchedule, setWorkingHoursSchedule] = useState<WorkingWeek>(props.vehicle.workingWeek ?? unrestrictedWorkingWeek);
    const prevUpdateStatus = usePrevious(props.workingWeekUpdateStatus);
    const { vehicle } = props;

    const setWorkingHoursDaySchedule = (weekday: WeekDay, timeKey: 'start' | 'end', time: Moment) => {
        const timeAsString = time ? time.format('HH:mm') : null;
        setWorkingHoursSchedule((prev) => ({
            ...prev!,
            schedule: {
                ...prev!.schedule,
                [weekday]: [{ ...prev!.schedule[weekday][0], [timeKey]: timeAsString }]
            }
        }))
    }

    const onToggleWeekDay = (weekday: WeekDay, e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setWorkingHoursSchedule((prev) => ({
            ...prev!,
            schedule: {
                ...prev!.schedule,
                [weekday]: checked ? [{ start: '00:00', end: '23:59' }] : []
            }
        }))
    }

    const invalidTimeSlot = checkForInvalidTimeSlots(workingHoursSchedule);

    return (
        <div id="working-hours-lp-modal" className="panel radius-rectangle">
            <div className="panel-header">
                <h2 className="bold-title">
                    <FontAwesomeIcon icon={faBriefcaseClock} size='sm' /><FormattedMessage id="WORKING_HOURS_LP_MODAL_TITLE" />&nbsp;<span className='bold'>{vehicle.localizedLicensePlate.licensePlate}</span>
                </h2>
                <button className="btn-close-modal" onClick={() => props.closeModal()} />
            </div>

            <div className="panel-content align-left">
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <table id="table-working-hours-lp">
                        <thead>
                            <tr>
                                <th className='bold'>{formatMessage({ id: 'WH_MODAL_COLUMN_STATUS' })}</th>
                                <th className='bold'>{formatMessage({ id: 'WH_MODAL_COLUMN_WEEKDAY' })}</th>
                                <th className='bold'>{formatMessage({ id: 'WH_MODAL_COLUMN_START_TIME' })}</th>
                                <th className='bold'>{formatMessage({ id: 'WH_MODAL_COLUMN_END_TIME' })}</th>
                                <th className='bold'>{formatMessage({ id: 'WH_MODAL_COLUMN_VISUALISATION' })}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orderedWeekdays.map((weekday) => {
                                return (
                                    <WeekDayTableRow
                                        key={weekday}
                                        weekday={weekday as WeekDay}
                                        daySchedule={workingHoursSchedule?.schedule[weekday]!}
                                        onChangeTime={setWorkingHoursDaySchedule}
                                        onToggleWeekDay={onToggleWeekDay}
                                        saving={props.workingWeekUpdateStatus.loading}
                                    />
                                )
                            })}
                        </tbody>
                    </table>
                </LocalizationProvider>

                <div className='buttons-group-v2'>
                    <div className='horizontal-line' />

                    {invalidTimeSlot && (
                        <ErrorMessageBox
                            errorMessage={`${formatMessage({ id: 'ERROR_INVALID_TIME_SLOT' })} ${formatMessage({ id: invalidTimeSlot.day })}`}
                            style={{ margin: '20px 0 0 0' }}
                        />
                    )}

                    {prevUpdateStatus?.loading && props.workingWeekUpdateStatus.error && (
                        <ErrorMessageBox errorMessage={formatMessage({ id: 'ERROR_SOMETHING_WENT_WRONG' })} style={{ margin: '20px 0 0 0' }} />
                    )}

                    <div className='flex-space-between'>

                        <div id="wh-timezone-info-text">
                            <p className="txt-16"><FormattedMessage id='MODAL_LP_WH_INFO_TIMEZONE' /></p>
                        </div>

                        <div>
                            <button onClick={props.closeModal} className='btn-cancel'>
                                {formatMessage({ id: 'GLOBAL_CANCEL' })}
                            </button>

                            {!!vehicle.workingWeek && (
                                <SubmitButton
                                    buttonText={formatMessage({ id: 'MODAL_LP_WH_BTN_DELETE' })}
                                    saving={props.workingWeekUpdateStatus.loading}
                                    onClick={() => props.updateLicensePlateWorkingWeek(vehicle, null)}
                                    classNames='btn-red'
                                />
                            )}

                            <SubmitButton
                                disabled={!!invalidTimeSlot}
                                buttonText={vehicle.workingWeek
                                    ? formatMessage({ id: 'MODAL_LP_WH_BTN_SAVE' })
                                    : formatMessage({ id: 'MODAL_LP_WH_BTN_ADD' })
                                }
                                saving={props.workingWeekUpdateStatus.loading}
                                onClick={() => props.updateLicensePlateWorkingWeek(vehicle, workingHoursSchedule)}
                            />
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};


type OwnProps = { vehicle: Vehicle }
type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
export type EditWorkingHoursLicensePlateModalProps = StateProps & DispatchProps & OwnProps;

/* istanbul ignore next */
function mapStateToProps(state: ReduxState) {
    return {
        companyDetails: state?.companyDetails?.companyDetails,
        token: state?.auth?.token,
        workingWeekUpdateStatus: state.companyVehicles.workingWeekUpdateStatus
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
    return bindActionCreators({
        closeModal: modalWindowActions.closeModal,
        updateLicensePlateWorkingWeek: vehiclesActions.updateLicensePlateWorkingWeek
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(EditWorkingHoursLicensePlateModal);